import "./ChangelogModal.scss";
import React, { FC, Fragment, useContext, useEffect, useMemo, useState } from "react";
import Button, { EButtonSize, EButtonType } from "../Buttons/Button";
import ModalV2 from "../Modal/ModalV2";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fetchConfig, selectConfig } from "../../Store/configSlice";
import { ConsentTypes } from "../Consent/Consents";
import ApiContext from "../../Api/ApiContext";
import { useAuth0 } from "@auth0/auth0-react";
import { AppDispatch } from "../../Store/store";

interface IChangelogModalProps {}
interface IChangelogProps {}

const requiredConsents = new Set(["ChangelogPlaybook"]);

const ChangelogModal: FC<IChangelogModalProps> = () => {
  const [showModal, setShowModal] = useState<boolean>(false);

  const { t } = useTranslation();

  const openModal = () => setShowModal(true);

  const config = useSelector(selectConfig);

  const consents = useMemo(() => ConsentTypes, []);

  const consentsNotGiven = useMemo(() => {
    if (!config) return [];

    const consentsGiven = new Set(config.consents || []);
    const now = Date.now();
    const userCreated = new Date(config.user?.created).getTime();

    return consents.filter(consent => {
      const start = new Date(consent.start).getTime();
      const end = new Date(consent.end).getTime();
      return !consentsGiven.has(consent.type) && start < now && end > now && userCreated < start;
    });
  }, [config, consents]);
  
  const api = useContext(ApiContext);
  const { getAccessTokenSilently } = useAuth0();
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    const hasRequiredConsents = consentsNotGiven.filter(consent => requiredConsents.has(consent.type));

    if (hasRequiredConsents.length > 0) {
      openModal();

      hasRequiredConsents.map(consent => {
        if (requiredConsents.has(consent.type)) {
          api.post("/consent/", {type: consent.type}).then(() => {
            getAccessTokenSilently().then(token => {
                dispatch(fetchConfig(token));
              });
        });
        }
      });
    }
  }, [api, consentsNotGiven, dispatch, getAccessTokenSilently]);

  const changelog = t('changelog', { returnObjects: true }) as {
    cta: string;
    date: string;
    title: string;
    content: Array<{
      title: string;
      subtitle?: string;
      paragraphs: string[];
    }>;
  };

  return (
    <Fragment>
      <Button
      className="changelog-button"
        text={changelog.cta}
        buttonType={EButtonType.SECONDARY}
        size={EButtonSize.NORMAL}
        style={{ borderWidth: 2, marginTop: 12 }}
        onClick={openModal}
      />
      <ModalV2
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={changelog.title}
        headerLeft={changelog.date}
      >
        <Changelog />
      </ModalV2>
    </Fragment>
  );
}

const Changelog: FC<IChangelogProps> = () => {
  const { t } = useTranslation();

  const changelog = t('changelog', { returnObjects: true }) as {
    content: Array<{
      title: string;
      subtitle?: string;
      paragraphs: string[];
    }>;
  };

  return (
    <div className="changelog-container">
      <img src="/changelog/changelog-playbook-light.png" alt="Review" className="changelog-image-light" />
      <img src="/changelog/changelog-playbook-dark.png" alt="Review" className="changelog-image-dark" />
      {changelog.content.map((item, index) => {
        return (
          <div key={index}>
            {index > 0 && <div className="divider-wrapper">
              <div className="divider"></div>
            </div>}
            <p className="title">{item.title}</p>
            <p className="changelog-subtitle">{item.subtitle}</p>
            {item.paragraphs.map((paragraph, index) => {
              return <p key={index} className="content-text">{paragraph}</p>
            })}
          </div>
        )
      })}
    </div>
  )
}

export default ChangelogModal;